import ApiService from "@/api/ApiService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { Box, CircularProgress, Paper, Tooltip, IconButton } from "@mui/material";


import dayjs from "dayjs";
import useRights from "@/shared/api/useRights";
import { usePersistantStore } from "@/stores/global";
import { Fragment } from "react/jsx-runtime";
import {
  Edit,
  East,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useMemo, useState } from "react";
import { PlantState } from "../calendar/listView/MixingPlantView";
import MaterialEditor from "../planning/dialogs/newMaterialOrder/MaterialEditor";

export default function Bauhof({}: {}) {
  const rights = useRights();
  const queryClient = useQueryClient();
  const userId = usePersistantStore((state) => state.userId);

  const region = usePersistantStore((state) => state.region);

  const materialQuery = useQuery<any, AxiosResponse>({
    queryKey: ["getBauhof", "" + region],
    queryFn: ApiService.getBauhofTransport(region),
  });

  const orderQuery = useQuery<any, AxiosResponse>({
    queryKey: ["getBauhofOrders", "" + region, userId],
    queryFn: ApiService.getBauhofOrders(region),
  });

  const [hidden, setHidden] = useState([]);

  function toggleHidden(date: string): void {
    setHidden((prevHidden) =>
      prevHidden.includes(date)
        ? prevHidden.filter((d) => d !== date)
        : [...prevHidden, date]
    );
  }

  const data = useMemo(() => {
    if (!orderQuery.data && !materialQuery.data) return {};

    const merged: Record<string, Record<string, any>> = {};

    const allDates = new Set<string>([
      ...(orderQuery.data && !Array.isArray(orderQuery.data) ? Object.keys(orderQuery.data) : []),
      ...(materialQuery.data && !Array.isArray(materialQuery.data) ? Object.keys(materialQuery.data) : []),
    ]);

    for (const date of allDates) {
      merged[date] = {};

      const orderProjects = Array.isArray(orderQuery.data) ? {} : orderQuery.data?.[date] || {};
      const materialProjects = Array.isArray(materialQuery.data) ? {} : materialQuery.data?.[date] || {};

      const allProjectIds = new Set<string>([
        ...Object.keys(orderProjects),
        ...Object.keys(materialProjects),
      ]);

      for (const projectId of allProjectIds) {
        const orderProject = orderProjects[projectId];
        const materialProject = materialProjects[projectId];

        const base = orderProject || materialProject;

        merged[date][projectId] = {
          ...base,
          orders: orderProject?.orders ?? [],
          content: materialProject?.content ?? [],
        };
      }
    }

    return merged;
  }, [orderQuery.data, materialQuery.data]);

  return (
    <Box
      sx={{
        overflow: "hidden",
        display: "flex",
        p: 1,
        flexDirection: "column",
      }}
    >
      <div className="calListWrapper">
        {materialQuery.isLoading || orderQuery.isLoading ? (
          <CircularProgress />
        ) : materialQuery.isLoadingError || orderQuery.isLoadingError ? (
          "Error"
        ) : (
          Object.entries(data)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, content]) => (
              <Fragment key={date}>
                <div
                  className="bh_dateheader"
                  onClick={() => toggleHidden(date)}
                >
                  {hidden.includes(date) ? (
                    <ExpandMore fontSize="large" />
                  ) : (
                    <ExpandLess fontSize="large" />
                  )}
                  <div className="bh_date">
                    {dayjs(date, "YYYY-MM-DD").format("ddd, DD.MM.YYYY")}
                  </div>
                </div>
                {hidden.includes(date) ? null : (
                  <div className="bh_grid">
                    {Object.values(content)
                      .sort(
                        (a, b) =>
                          a.type.localeCompare(b.type) ||
                          a.name.localeCompare(b.name)
                      )
                      .map((x) => (
                        <Paper
                          key={x.id}
                          className={"bh_paper bh_papertype" + x.type}
                        >
                          <div className="bh_xheader">
                            <div className="bh_title">
                              {!!x.number ? (
                                <span className="bh_number">{x.number}</span>
                              ) : null}
                              {x.name}
                            </div>
                            {!!x.crews ? (
                              <div className="bh_crews">
                                {x.crews.join("; ")}
                              </div>
                            ) : null}
                          </div>
                          <div className="bh_devicelist">
                            {!!x.orders && x.orders.length
                              ? x.orders.map((order) => (
                                  <div className="bh_compartment" key={order.id}>
                                    <div className="bho_articlewrap">
                                      <div className="bho_articles">
                                        {[
                                          {
                                            article: order.article,
                                            amount: order.amount,
                                            deleted: false,
                                          },
                                        ]
                                          .concat(order.additionalArticles)
                                          .map((a) =>
                                            a.deleted ? null : (
                                              <div className={"bh_resource"}>
                                                <div className="bh_easticon">
                                                  <East fontSize="small" />
                                                </div>
                                                <div className="bhname">
                                                  <span className="bh_number">
                                                    {a.amount} {a.article.unit}
                                                  </span>
                                                  {a.article.name}
                                                </div>
                                              </div>
                                            )
                                          )}
                                          {order.comment.length > 0 ? (
                                      <div className="bho_comment">
                                        {order.comment}
                                      </div>
                                    ) : null}
                                      </div>
                                      {"DISPOSITION" in rights && order.transportStatus !== "STORNO" && order.editorState?.job > 0 ? (
                                        <MaterialEditor
                                          jobId={order.editorState.job}
                                          projectId={x.id}
                                          initialState={order.editorState}
                                          saveType="DIRECT"
                                          invalidate={() => {
                                            for (const queryKey of [["getBauhofOrders"], ["getBauhof"],
                                              ["dispoCalJob", "" + order.editorState.job], ["getJobApp", "" + order.editorState.job]]) {
                                              queryClient.invalidateQueries({ queryKey });
                                            }
                                          }}
                                          Opener={({ onClick }) => (
                                            <IconButton size="small" aria-label="Bestellung bearbeiten" title="Bearbeiten" onClick={onClick}>
                                              <Edit fontSize="small" />
                                            </IconButton>
                                          )}
                                        />
                                      ) : null}
                                      <div className="bho_plantstate">
                                        <PlantState
                                          readOnly={false}
                                          isBauhof={true}
                                          id={order.id}
                                          state={order.plantStatus}
                                        />
                                      </div>
                                    </div>
                                    
                                  </div>
                                ))
                              : null}
                            {!!x.content && x.content.length ? (
                              <div className="bh_compartment">
                                {x.content
                                  .sort((a, b) =>
                                    a.number.localeCompare(b.number)
                                  )
                                  .map((resource) => (
                                    <Tooltip
                                      title={
                                        !!resource.meta ? (
                                          <div>
                                            zuvor bis{" "}
                                            {dayjs(
                                              resource.meta.date,
                                              "YYYY-MM-DD"
                                            ).format("ddd, DD.MM.YYYY")}{" "}
                                            bei{" "}
                                            {!!resource.meta.number ? (
                                              <span className="bh_number">
                                                {resource.meta.number}
                                              </span>
                                            ) : null}
                                            {resource.meta.name}{" "}
                                            {!!resource.meta.crews
                                              ? "(" +
                                                resource.meta.crews.join("; ") +
                                                ")"
                                              : ""}
                                          </div>
                                        ) : null
                                      }
                                    >
                                      <div
                                        className={
                                          "bh_resource" +
                                          (!!resource.meta &&
                                          !resource.meta.name.includes("Bauhof")
                                            ? " bh_resource_meta"
                                            : "")
                                        }
                                      >
                                        <div className="bh_easticon">
                                          <East fontSize="small" />
                                        </div>
                                        <div className="bhname">
                                          <span className="bh_number">
                                            {resource.number}
                                          </span>
                                          {resource.name}
                                        </div>
                                      </div>
                                    </Tooltip>
                                  ))}
                              </div>
                            ) : null}
                          </div>
                        </Paper>
                      ))}
                  </div>
                )}
              </Fragment>
            ))
        )}
      </div>
    </Box>
  );
}
